/**
 * Local archive flags for debtor records (by debtor id).
 * Independent of demand plan billing; archive records are unlimited.
 */

const STORAGE_KEY = 'demandjet.archivedDebtors';
const DELETED_STORAGE_KEY = 'demandjet.deletedDebtors';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Map of debtorId → { archivedAt } */
export function loadArchivedDebtorMap() {
  const map = readJson(STORAGE_KEY, null);
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    return { ...map };
  }
  return {};
}

export function saveArchivedDebtorMap(map) {
  const next = map && typeof map === 'object' ? map : {};
  writeJson(STORAGE_KEY, next);
  return next;
}

/** Map of debtorId → { deletedAt } for permanently removed contacts. */
export function loadDeletedDebtorMap() {
  const map = readJson(DELETED_STORAGE_KEY, null);
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    return { ...map };
  }
  return {};
}

export function saveDeletedDebtorMap(map) {
  const next = map && typeof map === 'object' ? map : {};
  writeJson(DELETED_STORAGE_KEY, next);
  return next;
}

export function excludeDeletedDebtors(debtors = [], deletedMap = loadDeletedDebtorMap()) {
  return (debtors || []).filter((debtor) => {
    const id = String(debtor?.id || '');
    return id && !deletedMap[id];
  });
}

export function applyArchivedDebtorFlags(debtors = [], archivedMap = loadArchivedDebtorMap()) {
  return (debtors || []).map((debtor) => {
    const id = String(debtor?.id || '');
    const meta = archivedMap[id];
    const isArchived = Boolean(debtor?.Archived) || Boolean(meta);
    return {
      ...debtor,
      Archived: isArchived,
      Archived_At: debtor?.Archived_At || meta?.archivedAt || null,
    };
  });
}

export function activeDebtors(debtors = []) {
  return (debtors || []).filter((debtor) => !debtor.Archived);
}

export function archivedDebtors(debtors = []) {
  return (debtors || [])
    .filter((debtor) => debtor.Archived)
    .sort((a, b) => String(b.Archived_At || '').localeCompare(String(a.Archived_At || '')));
}

/**
 * Archive selected debtors by id.
 * @returns {{ updated: object[], map: object }}
 */
export function archiveDebtors(debtors = [], archivedMap = loadArchivedDebtorMap()) {
  const map = { ...archivedMap };
  const now = new Date().toISOString();
  const updated = [];

  for (const debtor of debtors || []) {
    const id = String(debtor?.id || '');
    if (!id) continue;
    if (debtor.Archived || map[id]) continue;
    map[id] = { archivedAt: now };
    updated.push({
      ...debtor,
      Archived: true,
      Archived_At: now,
    });
  }

  saveArchivedDebtorMap(map);
  return { updated, map };
}

/**
 * Restore selected debtors to the active list.
 * @returns {{ updated: object[], map: object }}
 */
export function unarchiveDebtors(debtors = [], archivedMap = loadArchivedDebtorMap()) {
  const map = { ...archivedMap };
  const updated = [];

  for (const debtor of debtors || []) {
    const id = String(debtor?.id || '');
    if (!id) continue;
    delete map[id];
    updated.push({
      ...debtor,
      Archived: false,
      Archived_At: null,
    });
  }

  saveArchivedDebtorMap(map);
  return { updated, map };
}

export function selectedDebtorsFromIds(debtors = [], selectedIds) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  return (debtors || []).filter((debtor) => ids.has(String(debtor.id)));
}

/**
 * Permanently remove archived debtor contacts from the portal lists.
 * Clears archive flags and records deleted ids so matter-backed keys stay hidden.
 * @returns {{ updated: object[], archivedMap: object, deletedMap: object }}
 */
export function permanentlyDeleteDebtors(
  debtors = [],
  {
    archivedMap = loadArchivedDebtorMap(),
    deletedMap = loadDeletedDebtorMap(),
  } = {}
) {
  const nextArchived = { ...archivedMap };
  const nextDeleted = { ...deletedMap };
  const now = new Date().toISOString();
  const updated = [];

  for (const debtor of debtors || []) {
    const id = String(debtor?.id || '');
    if (!id) continue;
    delete nextArchived[id];
    nextDeleted[id] = { deletedAt: now };
    updated.push({
      ...debtor,
      Archived: false,
      Archived_At: null,
      Deleted: true,
      Deleted_At: now,
    });
  }

  saveArchivedDebtorMap(nextArchived);
  saveDeletedDebtorMap(nextDeleted);
  return {
    updated,
    archivedMap: nextArchived,
    deletedMap: nextDeleted,
  };
}

/**
 * When a debtor identity key changes, move the archive flag to the new id.
 */
export function remapArchivedDebtorId(previousId, nextId, archivedMap = loadArchivedDebtorMap()) {
  const prev = String(previousId || '');
  const next = String(nextId || '');
  if (!prev || !next || prev === next) return archivedMap;
  if (!archivedMap[prev]) return archivedMap;
  const map = { ...archivedMap };
  map[next] = map[prev];
  delete map[prev];
  saveArchivedDebtorMap(map);
  return map;
}
